import React, { useCallback, useMemo, useState } from "react";
import { localizedPath } from "../../../core/localePaths";
import { pageGutterPx } from "../../../core/breakpoints";
import { C } from "../../../core/constants";
import {
  buildFormInputStyle,
  FORM_FIELD_CELL_CLASS,
  formFieldCellStyle,
  formFieldGridClassName,
  formFieldGridStyle,
} from "../../../core/formFields";
import NativePickerInput from "../../../core/NativePickerInput";
import { validatePartnerBirthDates } from "../../../core/partnerBirthForm";
import { APP_FONT_SIZE } from "../../../core/theme";
import { useIsMobile } from "../../../hooks/useMediaQuery";
import { usePredictionRun } from "../../../hooks/usePredictionRun";
import { gaTrackCta } from "../../../analytics/ga4";
import { useI18n } from "../../i18n/LocaleProvider";
import { buildCoupleMatchPdfHtml } from "../../export/coupleMatchPdf";
import { createPdfFileName, printHtmlAsPdf } from "../../export/pdfUtils";
import { createCoupleMatchGeneratePrediction, fetchPredictionById } from "../../predictions/api";
import { computeCoupleMatch } from "../coupleMatchEngine";
import { BIRTH_HOUR_PERIOD_OPTIONS, birthHourToSelectValue } from "../coupleMatchBazi";
import { buildCoupleMatchNarratives, resolveCoupleMatchLine } from "../coupleMatchNarrative";
import Button from "../../../ui/Button";
import GlassCard from "../../../ui/GlassCard";
import PredictionAccessPopup from "../../../ui/PredictionAccessPopup";
import {
  DaYunTimeline,
  DimensionBars,
  DualPillarCards,
  ElementBalanceCompare,
  JointYearsStrip,
  LifeTogetherCard,
  OverallScoreRing,
  PillarMatrixHeatmap,
} from "./CoupleMatchCharts";

function GoldText({ children, accent }) {
  return <span style={{ color: accent, textShadow: `0 0 18px ${accent}66` }}>{children}</span>;
}

export default function CoupleMatchPage({ acc, t, replayPredictionId, authUser, onCreditsUpdate, onResetPage }) {
  const { locale } = useI18n();
  const fs = APP_FONT_SIZE;
  const isMobile = useIsMobile();
  const gutter = pageGutterPx(isMobile);
  const isReplayMode = Boolean(replayPredictionId);

  const [partnerA, setPartnerA] = useState({ name: "", birthDate: "", birthHour: "" });
  const [partnerB, setPartnerB] = useState({ name: "", birthDate: "", birthHour: "" });
  const [result, setResult] = useState(null);
  const [analyzed, setAnalyzed] = useState(false);
  const [formError, setFormError] = useState("");
  const [loadingReplay, setLoadingReplay] = useState(false);
  const [needsReanalyze, setNeedsReanalyze] = useState(false);
  const [lastChargedCredits, setLastChargedCredits] = useState(null);

  const hasActiveSubscription = Boolean(authUser?.activeSubscription ?? authUser?.active_subscription);
  const creditCostRaw = Number(authUser?.predictionCosts?.couple_match);
  const creditCost = Number.isFinite(creditCostRaw) && creditCostRaw > 0 ? creditCostRaw : null;

  const interpolate = useCallback((template, vars = {}) => {
    return Object.entries(vars).reduce((text, [key, value]) => {
      return text.replace(new RegExp(`%\\{${key}\\}`, "g"), String(value ?? ""));
    }, template);
  }, []);

  const analyzeLabel = hasActiveSubscription
    ? t("couple_match_analyze_cta")
    : creditCost
      ? interpolate(t("couple_match_analyze_cta_cost"), { credits: creditCost })
      : t("couple_match_analyze_cta");

  const creditsUsedLabel = useMemo(() => {
    const charged = Number(lastChargedCredits);
    if (!Number.isFinite(charged)) return null;
    if (charged === 0) return t("couple_match_credits_included_subscription");
    return interpolate(t("couple_match_credits_used_dynamic"), { credits: charged });
  }, [interpolate, lastChargedCredits, t]);

  const validateInputs = useCallback(() => {
    return validatePartnerBirthDates(partnerA, partnerB, {
      required: t("couple_match_error_required_dob"),
      invalid: t("couple_match_error_invalid_dob"),
      future: t("couple_match_error_future_dob"),
    });
  }, [partnerA, partnerB, t]);

  const runAnalyze = useCallback(async () => {
    const computed = computeCoupleMatch(partnerA, partnerB, { t, locale });
    const { narratives: _narratives, ...storedResult } = computed;
    const predictionResponse = await createCoupleMatchGeneratePrediction({
      input_payload: {
        partner_a: partnerA,
        partner_b: partnerB,
      },
      result_payload: {
        analyzed: true,
        ...storedResult,
      },
    });
    return { predictionResponse, computed };
  }, [partnerA, partnerB, t, locale]);

  const {
    loading,
    showAccessPopup,
    setShowAccessPopup,
    accessPopupCopy,
    execute,
  } = usePredictionRun({
    isReplayMode,
    authUser,
    onCreditsUpdate,
    t,
    runAnalyze,
  });

  const invalidate = useCallback(() => {
    if (!analyzed || isReplayMode) return;
    setAnalyzed(false);
    setNeedsReanalyze(true);
    setResult(null);
    setLastChargedCredits(null);
  }, [analyzed, isReplayMode]);

  React.useEffect(() => {
    if (!replayPredictionId) return;
    let cancelled = false;
    async function loadReplay() {
      setLoadingReplay(true);
      try {
        const response = await fetchPredictionById(replayPredictionId);
        if (cancelled) return;
        const prediction = response?.prediction;
        if (!prediction || prediction.feature !== "couple_match") return;
        const input = prediction.input_payload || {};
        const payload = prediction.result_payload || {};
        setPartnerA(input.partner_a || input.partnerA || partnerA);
        setPartnerB(input.partner_b || input.partnerB || partnerB);
        setResult(payload.overall_score != null ? payload : null);
        setAnalyzed(true);
        setNeedsReanalyze(false);
      } catch (_) {
        // ignore
      } finally {
        if (!cancelled) setLoadingReplay(false);
      }
    }
    loadReplay();
    return () => {
      cancelled = true;
    };
  }, [replayPredictionId]);

  const handleAnalyze = async () => {
    if (loading || isReplayMode) return;
    const validationError = validateInputs();
    if (validationError) {
      setFormError(validationError);
      return;
    }
    setFormError("");
    gaTrackCta({
      cta_id: "couple_match_analyze",
      cta_label: analyzeLabel,
      destination: "couple_match",
    });
    const outcome = await execute();
    if (outcome.ok && outcome.computed) {
      setResult(outcome.computed);
      setAnalyzed(true);
      setNeedsReanalyze(false);
      setLastChargedCredits(outcome.chargedCredits);
    } else if (outcome.error) {
      setFormError(t("couple_match_error_failed"));
    }
  };

  const exportCoupleMatchToPdf = () => {
    if (!result || !analyzed) return;
    const now = new Date();
    const exportFileName = createPdfFileName("couple-match", now);
    const html = buildCoupleMatchPdfHtml({
      exportedAt: now.toLocaleString(),
      partnerA,
      partnerB,
      result,
      narratives,
      theme: {
        primary: acc.primary,
        secondary: acc.secondary,
        tertiary: acc.tertiary,
      },
      labels: {
        report: t("pdf_report_title"),
        eyebrow: t("pdf_couple_match_eyebrow"),
        title: t("pdf_couple_match_title"),
        exported: t("pdf_exported"),
        generatedBy: t("pdf_generated_by"),
        overall: t("couple_match_overall_label"),
        dimensions: t("couple_match_dimensions_title"),
        lifeTogether: t("couple_match_life_together_title"),
        partnerA: partnerA.name || t("couple_match_chart_first"),
        partnerB: partnerB.name || t("couple_match_chart_second"),
        matrixSection: t("couple_match_matrix_title"),
        elementsSection: t("couple_match_elements_reading_title"),
        topYearsSection: t("couple_match_joint_years_title"),
        strengths: t("couple_match_life_strengths_title"),
        watchouts: t("couple_match_life_watchouts_title"),
        plan: t("couple_match_life_plan_title"),
      },
      t,
    });
    printHtmlAsPdf({ html, title: exportFileName.replace(/\.pdf$/i, "") });
  };

  const goToNewReading = () => {
    if (onResetPage) {
      onResetPage();
      return;
    }
    window.location.assign(localizedPath("coupleMatch", locale));
  };

  const showResults = analyzed && result && !loading;
  const exportPdfButton = showResults ? (
    <Button variant="outline" acc={acc} onClick={exportCoupleMatchToPdf}>
      {t("export_result_pdf")}
    </Button>
  ) : null;

  const fieldBorder = formError ? "rgba(255,120,120,0.55)" : "rgba(255,255,255,0.12)";
  const inputStyle = useMemo(() => buildFormInputStyle({ fontSize: fs }), [fs]);
  const birthDateInputStyle = useMemo(
    () => buildFormInputStyle({ fontSize: fs, extra: { border: `1px solid ${fieldBorder}` } }),
    [fs, fieldBorder]
  );
  const currentCalendarYear = new Date().getFullYear();

  const narratives = useMemo(() => {
    if (!result) return null;
    return buildCoupleMatchNarratives(result, t, { locale, currentYear: currentCalendarYear });
  }, [result, t, locale, currentCalendarYear]);

  const jointYears = narratives?.jointYears || result?.joint_timeline || [];

  const renderPartnerFields = (side, partner, setPartner, labelKey) => (
    <GlassCard style={{ padding: 24, minWidth: 0, width: "100%" }} accent={side === "a" ? acc.primary : acc.secondary}>
      <div style={{ fontSize: 12, letterSpacing: "0.12em", color: acc.secondary, marginBottom: 16, textTransform: "uppercase" }}>
        {t(labelKey)}
      </div>
      <div className={formFieldGridClassName({ stacked: true })} style={formFieldGridStyle({ stacked: true })}>
        <div className={FORM_FIELD_CELL_CLASS} style={formFieldCellStyle()}>
          <label style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.08em" }}>{t("couple_match_name_optional")}</label>
          <input
            type="text"
            value={partner.name}
            disabled={isReplayMode}
            onChange={(e) => {
              setPartner({ ...partner, name: e.target.value });
            }}
            style={inputStyle}
            placeholder={t("couple_match_name_placeholder")}
          />
        </div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "minmax(0, 1.2fr) minmax(0, 1fr)",
            gap: 16,
            gridColumn: "1 / -1",
          }}
        >
          <div className={FORM_FIELD_CELL_CLASS} style={formFieldCellStyle()}>
            <label style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.08em" }}>{t("couple_match_birth_date")}</label>
            <NativePickerInput
              type="date"
              value={partner.birthDate}
              disabled={isReplayMode}
              onChange={(e) => {
                setPartner({ ...partner, birthDate: e.target.value });
                setFormError("");
                invalidate();
              }}
              style={birthDateInputStyle}
            />
          </div>
          <div className={FORM_FIELD_CELL_CLASS} style={formFieldCellStyle()}>
            <label style={{ fontSize: 11, color: C.textMuted, letterSpacing: "0.08em" }}>{t("couple_match_birth_hour")}</label>
            <select
              value={birthHourToSelectValue(partner.birthHour)}
              disabled={isReplayMode}
              onChange={(e) => {
                setPartner({ ...partner, birthHour: e.target.value });
                invalidate();
              }}
              style={inputStyle}
            >
              {BIRTH_HOUR_PERIOD_OPTIONS.map((option) => (
                <option key={option.period} value={option.isDefault ? "" : String(option.value)}>
                  {option.isDefault
                    ? resolveCoupleMatchLine(t, "couple_match_hour_period_default", { period: option.period })
                    : option.period}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>
    </GlassCard>
  );

  return (
    <div
      style={{
        padding: gutter,
        width: "100%",
        maxWidth: 1100,
        minWidth: 0,
        margin: "0 auto",
        boxSizing: "border-box",
      }}
    >
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: "clamp(30px,4vw,44px)", fontWeight: 400, color: C.textPrimary }}>
          {t("couple_match_title_prefix")} <GoldText accent={acc.secondary}>{t("couple_match_title_highlight")}</GoldText>
        </h1>
        <p style={{ color: C.textSecondary, fontSize: fs, marginTop: 10, maxWidth: 640, lineHeight: 1.65 }}>
          {t("couple_match_subtitle")}
        </p>
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, 1fr))",
          gap: 20,
          marginBottom: 24,
        }}
      >
        {renderPartnerFields("a", partnerA, setPartnerA, "couple_match_chart_first")}
        {renderPartnerFields("b", partnerB, setPartnerB, "couple_match_chart_second")}
      </div>

      {formError ? (
        <div style={{ marginBottom: 14, fontSize: 12, color: "#ff8e9f" }}>{formError}</div>
      ) : null}
      {needsReanalyze ? (
        <div style={{ marginBottom: 14, fontSize: 12, color: C.textSecondary }}>{t("couple_match_reanalyze_hint")}</div>
      ) : null}

      <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap", marginBottom: 32 }}>
        {!isReplayMode ? (
          <>
            <Button
              variant="primary"
              acc={acc}
              onClick={handleAnalyze}
              disabled={loading}
              style={{ padding: "13px 40px", fontSize: fs }}
            >
              {loading ? t("couple_match_loading") : analyzeLabel}
            </Button>
            {showResults && creditsUsedLabel ? (
              <div style={{ fontSize: 13, color: C.textMuted }}>
                <span style={{ color: acc.primary }}>✦</span> {creditsUsedLabel}
              </div>
            ) : null}
            {exportPdfButton}
          </>
        ) : (
          <>
            <div style={{ color: C.textMuted, fontSize: 13 }}>
              <span style={{ color: acc.primary }}>✦</span>{" "}
              {loadingReplay ? t("couple_match_loading_replay") : t("dashboard_replay_mode_badge")}
            </div>
            {!loadingReplay && (
              <Button variant="primary" acc={acc} onClick={goToNewReading}>
                {t("dashboard_replay_start_new_couple_match")}
              </Button>
            )}
            {exportPdfButton}
          </>
        )}
      </div>

      {showResults && (
        <div style={{ display: "grid", gap: 24, minWidth: 0, marginBottom: isMobile ? 16 : 0 }}>
          <GlassCard
            style={{
              padding: isMobile ? 20 : 28,
              display: "flex",
              flexWrap: "wrap",
              gap: isMobile ? 20 : 32,
              alignItems: "center",
              minWidth: 0,
            }}
            glow
            accent={acc.secondary}
          >
            <OverallScoreRing score={result.overall_score} band={result.overall_band} acc={acc} t={t} />
            <div style={{ flex: "1 1 200px", minWidth: 0 }}>
              <h2 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 24, color: C.textPrimary, marginBottom: 8 }}>
                {t("couple_match_overall_label")}
              </h2>
              <p style={{ color: C.textSecondary, fontSize: fs - 1, lineHeight: 1.65, overflowWrap: "anywhere" }}>
                {t(`couple_match_animal_${result.animal_summary.yearRelation}`)} · {t(`couple_match_animal_${result.animal_summary.dayRelation}`)}
              </p>
            </div>
          </GlassCard>

          <GlassCard style={{ padding: isMobile ? 20 : 28, minWidth: 0 }} accent={acc.primary}>
            <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: C.textPrimary, marginBottom: 16 }}>
              {t("couple_match_dimensions_title")}
            </h3>
            <DimensionBars dimensions={result.dimensions} acc={acc} t={t} />
          </GlassCard>

          <GlassCard style={{ padding: isMobile ? 20 : 28, minWidth: 0 }} accent={acc.tertiary}>
            <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: C.textPrimary, marginBottom: 16 }}>
              {t("couple_match_pillars_title")}
            </h3>
            <DualPillarCards
              partners={result.partners}
              narratives={narratives}
              acc={acc}
              t={t}
              locale={locale}
              isMobile={isMobile}
            />
          </GlassCard>

          <GlassCard style={{ padding: isMobile ? 20 : 28, minWidth: 0 }} accent={acc.primary}>
            <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: C.textPrimary, marginBottom: 16 }}>
              {t("couple_match_matrix_title")}
            </h3>
            <PillarMatrixHeatmap
              matrix={result.pillar_matrix}
              narratives={narratives}
              acc={acc}
              t={t}
              isMobile={isMobile}
              locale={locale}
            />
          </GlassCard>

          <GlassCard style={{ padding: isMobile ? 20 : 28, minWidth: 0 }} accent={acc.secondary}>
            <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: C.textPrimary, marginBottom: 16 }}>
              {t("couple_match_elements_title")}
            </h3>
            <ElementBalanceCompare
              partners={result.partners}
              narratives={narratives}
              acc={acc}
              t={t}
              locale={locale}
              isMobile={isMobile}
            />
          </GlassCard>

          <GlassCard style={{ padding: isMobile ? 20 : 28, minWidth: 0 }} accent={acc.tertiary}>
            <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: C.textPrimary, marginBottom: 16 }}>
              {t("couple_match_dayun_title")}
            </h3>
            <DaYunTimeline narratives={narratives} acc={acc} t={t} locale={locale} isMobile={isMobile} />
          </GlassCard>

          <GlassCard style={{ padding: isMobile ? 20 : 28, minWidth: 0 }} accent={acc.primary}>
            <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: C.textPrimary, marginBottom: 16 }}>
              {t("couple_match_joint_years_title")}
            </h3>
            <JointYearsStrip years={jointYears} acc={acc} t={t} defaultYear={currentCalendarYear} locale={locale} />
          </GlassCard>

          <GlassCard style={{ padding: isMobile ? 20 : 28, minWidth: 0 }} glow accent={acc.secondary}>
            <h3 style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, color: C.textPrimary, marginBottom: 12 }}>
              {t("couple_match_life_together_title")}
            </h3>
            <LifeTogetherCard lifeTogether={narratives?.lifeTogether} acc={acc} t={t} />
          </GlassCard>
        </div>
      )}

      <PredictionAccessPopup
        open={showAccessPopup}
        title={accessPopupCopy.title}
        message={accessPopupCopy.message}
        onClose={() => setShowAccessPopup(false)}
        onGoToPricing={() => {
          window.location.assign(localizedPath("creditsPlans", locale));
        }}
        acc={acc}
      />
    </div>
  );
}
